import {
  useWidgetContext,
  useWidgetEvents,
  useWidgetExpandCollapse,
  useWidgetLifecycleHandlers,
} from '@ptek/widget-core';
import {
  ComponentType,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { AppController } from '.';
import { WhisperClient, authManager } from '../api-clients/whisperClient';
import { getAuthConfig } from '../config/auth';
import { TAB_IDS } from '../constants/app';
import { WidgetAuthProvider, useWidgetAuth } from '../context/AuthContext';
import { FontSizeProvider } from '../context/FontSizeContext';
import { WidgetThemeProvider } from '../context/WidgetThemeProvider';
import { AuthGuard } from '../features/Auth';
import { DialogRenderer } from '../features/Dialogs/components';
import { useExitFlow } from '../features/Dialogs/hooks/useExitFlow';
import { useDatadogRumUserContext } from '../hooks/useDatadogRumUserContext';
import { useWidgetConfigTelemetry } from '../hooks/useWidgetConfigTelemetry';
import { useRecordingActions } from '../hooks/useRecordingActions';
import { useTemplateState } from '../hooks/useTemplateState';
import { useRecordingStore } from '../stores';
import { useDialogStore } from '../stores/dialogStore';
import { HEALTH_STATUS_TYPES, HealthStatus } from '../types/healthStatus';
import { DatadogErrorBoundary } from './DatadogErrorBoundary';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { ContestEntriesStatus } from '../api-clients/contestEntries';
import { healthStatusClient } from '../api-clients/healthStatus';
import { WIDGET_EVENTS } from '../constants/events';
import { useUserpilotInit } from '../hooks';
import { DEFAULT_CONTEST_STATUS } from '../types/contest';
import { FEATURE_FLAGS } from '../types/featureFlags';
import type { AmbientScribeContext } from '../types/widget';
import { isFeatureEnabled } from '../utils/feature-util';

// WHY: the 2026 giveaway ended 2026-07-01 and is disabled server-side, so the
// widget no longer calls the contest endpoints (or waits on them) at startup.
// Remove along with the rest of the contest feature.
const NO_CONTEST_ENTRIES: ContestEntriesStatus = {
  entriesEarned: 0,
  todayEntryEarned: false,
};

const AppContent: ComponentType = () => {
  const auth = useWidgetAuth();
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();
  const { onHostEvent, emitToHost } = useWidgetEvents();
  const { collapse, expand, isExpanded } = useWidgetExpandCollapse();
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [activeTab, setActiveTab] = useState<string>(TAB_IDS.transcript);
  const userSwitchedTabRef = useRef(false);
  const pausedCloseRequestedRef = useRef(false);

  const handleTabChange = useCallback(
    (newTabId: string) => {
      userSwitchedTabRef.current = true;
      if (!isExpanded) {
        expand();
      }
      setActiveTab(newTabId);
    },
    [expand, isExpanded]
  );

  useUserpilotInit();
  useDatadogRumUserContext();
  useWidgetConfigTelemetry();

  const features = widgetContext.context?.features;
  const unifiedSessionIdFlag = isFeatureEnabled(
    features,
    FEATURE_FLAGS.UNIFIED_SESSION_ID
  );
  useLayoutEffect(() => {
    const store = useRecordingStore.getState();
    store.setUnifiedSessionIdEnabled(unifiedSessionIdFlag);
  }, [unifiedSessionIdFlag]);

  const { templates } = useTemplateState();

  const isCurrentlyRecording = useRecordingStore((state) => state.isRecording);

  const isPaused = useRecordingStore((state) => state.isPaused);

  const callAbortRecording = useRecordingStore(
    (state) => state.callAbortRecording
  );

  const resetRecordingStore = useRecordingStore((state) => state.resetStore);

  useEffect(() => {
    const handleWidgetReady = () => {
      setTimeout(() => {
        collapse();
      }, 100);
    };

    const cleanup = onHostEvent('ready', handleWidgetReady);

    return cleanup;
  }, [onHostEvent, collapse]);

  const recordingActions = useRecordingActions();

  const exitFlow = useExitFlow({
    isCurrentlyRecording,
    onSaveAndExit: recordingActions.handleSaveAndExit,
    onConfirmClose: () => {
      emitToHost(WIDGET_EVENTS.EMITTER_NAMES.REQUEST_CLOSE);
    },
    onResetStore: () => {
      resetRecordingStore();
    },
  });

  const zustandDialogStore = useDialogStore();

  useEffect(() => {
    return () => {
      useDialogStore.getState().resetStore();
    };
  }, []);

  useWidgetLifecycleHandlers({
    beforeUnmount: () => {
      if (isCurrentlyRecording) {
        // Paused recording: "auto-save & close". The host is ending the
        // encounter, so silently run the existing abort/checkpoint path to
        // preserve the transcript (restored on reopen) and allow the unmount —
        // no confirm dialog. beforeUnmount is synchronous and BaseWidget tears
        // down the React tree from this boolean return, so we cannot await the
        // checkpoint. It is safe to fire-and-forget: callAbortRecording runs on
        // the module-level audioRecorder/sessionsClient singletons and the
        // module-level recording store (no persist middleware), none of which
        // are torn down by React unmount, and the paused branch does not reset
        // the store, so recordingSessionId/whisperSessionId survive for the
        // in-flight transcript upload that reopen-hydration reads server-side.
        // Also emit REQUEST_CLOSE; the ref absorbs the host's re-entrant
        // unmount(). Abort must run first: nested teardown clears
        // abortRecordingFn.
        if (isPaused) {
          if (pausedCloseRequestedRef.current) return true;
          pausedCloseRequestedRef.current = true;
          void callAbortRecording();
          emitToHost(WIDGET_EVENTS.EMITTER_NAMES.REQUEST_CLOSE);
          return true;
        }
        exitFlow.handleBeforeClose(true);
        return false;
      }
      exitFlow.handleBeforeClose(true);
      return true;
    },
    beforeHide: () => {
      if (isCurrentlyRecording) {
        exitFlow.handleBeforeClose(false);
        return false;
      }
      exitFlow.handleBeforeClose(false);
      return true;
    },
  });

  useEffect(() => {
    authManager.setTokenGetter(async () => {
      return await auth.getAccessToken();
    });
  }, [auth, auth.isAuthenticated, auth.user]);

  useEffect(() => {
    return () => {
      WhisperClient.resetInstance();
    };
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let isMounted = true;

    const checkHealth = async () => {
      const token = await auth.getAccessToken();
      if (!token || !isMounted) {
        return;
      }

      const status = await healthStatusClient.getHealthStatus();

      if (status.status !== HEALTH_STATUS_TYPES.healthy) {
        expand();
      }

      setHealthStatus(status);

      timeoutId = setTimeout(checkHealth, 30000);
    };

    if (auth.isAuthenticated) {
      checkHealth();
    }

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [auth, auth.isAuthenticated, expand]);

  return (
    <AuthGuard>
      <AppController
        auth={auth}
        templates={templates}
        healthStatus={healthStatus}
        contestStatus={DEFAULT_CONTEST_STATUS}
        contestEntries={NO_CONTEST_ENTRIES}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        handleTabChange={handleTabChange}
        userSwitchedTabRef={userSwitchedTabRef}
      />

      {/* Unified Zustand Dialog Renderer for All Dialogs */}
      <DialogRenderer
        activeDialogs={zustandDialogStore.activeDialogs}
        getDialogData={zustandDialogStore.getDialogData}
        closeDialog={zustandDialogStore.closeDialog}
      />
    </AuthGuard>
  );
};

/**
 * Root App component.
 * Rendered inside BrandThemeProvider by widget-core's BaseWidget.
 *
 * Provider hierarchy:
 * - BrandThemeProvider (widget-core, set up by BaseWidget)
 *   - WidgetThemeProvider (creates MUI theme from brand tokens)
 *     - DatadogErrorBoundary
 *       - WidgetAuthProvider
 *         - FontSizeProvider
 *           - AppContent
 */
const App: ComponentType = () => {
  return (
    <WidgetThemeProvider>
      <DatadogErrorBoundary>
        <WidgetAuthProvider
          config={getAuthConfig()}
          requireAuth={Boolean(import.meta.env.VITE_AUTH_REQUIRED === 'true')}
          tenantId={import.meta.env.VITE_TENANT_ID}
        >
          <FontSizeProvider>
            <AppContent />
          </FontSizeProvider>
        </WidgetAuthProvider>
      </DatadogErrorBoundary>
    </WidgetThemeProvider>
  );
};

export default App;
